import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'react-use';

import { maximumOrderTotal } from '../../constants';
import { Checkout } from '../../containers';
import { ProductQuantitySubmitFormData } from '../../containers/@forms/CartItemForm/validations';
import { GTMProduct as GTMProductInterface } from '../../entities/@api/Sylius/Resource/GTM';
import { CheckoutAddress, CheckoutDetailsFormData, CheckoutFormData } from '../../entities/@forms/Checkout/Checkout';
import { CouponCodeFormData } from '../../entities/@forms/CouponCodeForm/CouponCodeForm';
import { NewsletterFormData } from '../../entities/@forms/NewsletterForm/NewsletterForm';
import { PaymentStepFormData } from '../../entities/@forms/PaymentForm/PaymentForm';
import { CheckoutTab } from '../../entities/@shop/Checkout/Checkout';
import { Address, AddressType } from '../../entities/Address/Address';
import { defaultCountry } from '../../entities/Country/Country';
import { GTMEvent } from '../../entities/GTM/GTM';
import { generateCartItemToGTMProduct, generateGTMCart } from '../../entities/GTM/GTMRequests';
import { sendGTMEvent } from '../../entities/GTM/GTMService';
import { LinkTarget } from '../../entities/Link/Link';
import { AppRoute, appRoutes } from '../../entities/Routing/Routing';
import { isSSR } from '../../helpers';
import { useTimeout, useTrans } from '../../hooks';
import { MOCK_USPS } from '../../mock/mock-data/usp';
import { fetchCustomer } from '../../redux/@account/customer/customerActions';
import { registerUserDuringCheckout } from '../../redux/@account/registration/registrationActions';
import { signUpForNewsletter } from '../../redux/@forms/newsletterForm/newsletterFormActions';
import { clearCart, removeProductVariantFromCart, updateProductVariantInCart } from '../../redux/@shop/cart/cartActions';
import {
    addCouponCodeToOrder,
    connectOrderToCustomer,
    getPaymentMethods,
    payOrder,
    removeCouponCodeFromOrder,
    updateOrder,
} from '../../redux/@shop/order/orderActions';
import { useTypedDispatch, useTypedSelector } from '../../redux/store';

interface ConnectedCheckoutProps {
    tab?: CheckoutTab;
}

export const ConnectedCheckout: FC<ConnectedCheckoutProps> = ({
    tab,
}): ReactElement => {
    const trans = useTrans();
    const dispatch = useTypedDispatch();
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const isAuthenticated = useTypedSelector(state => state.authenticationSlice.isAuthenticated);

    const customer = useTypedSelector(state => state.customerSlice.customer);

    const cartIsInitiallyLoading = useTypedSelector(state => state.cartSlice.isInitiallyLoading);
    const cartIsLoading = useTypedSelector(state => state.cartSlice.isLoading);

    const orderIsLoading = useTypedSelector(state => state.orderSlice.isLoading);
    const order = useTypedSelector(state => state.orderSlice.order);

    const registrationError = useTypedSelector(state => state.registrationSlice.error);

    const isLoading = cartIsLoading || orderIsLoading;

    const [orderAddresses, setOrderAddresses] = useState<Address[]>([]);
    const [checkoutFormData, setCheckoutFormData] = useState<CheckoutFormData>();
    const [paymentStepData, setPaymentStepData] = useState<PaymentStepFormData>();
    const [isRedirectingToPay, setIsRedirectingToPay] = useState<boolean>(false);

    const totalExceedsMaximum = order.totalPrice >= maximumOrderTotal;

    const cart = useMemo((): GTMProductInterface => generateGTMCart(order), [order]);

    useEffect((): void => {
        if (isAuthenticated) {
            dispatch(fetchCustomer());
        }
    }, [isAuthenticated]);

    useEffect((): void => {
        if (customer) {
            dispatch(connectOrderToCustomer());
        }
    }, [customer]);

    useDebounce((): void => {
        if (tab === CheckoutTab.order) {
            if (cart.items.length === 0) {
                return;
            }

            sendGTMEvent(GTMEvent.viewCart, cart);
        }
    }, 100, [cart]);

    useEffect((): void => {
        if (order.addresses?.length) {
            setOrderAddresses(order.addresses);
            return;
        }

        if (customer) {
            setOrderAddresses(customer.addresses);
        }
    }, [order, customer]);

    useEffect((): void => {
        if (tab === CheckoutTab.payment && order.id && order.items.length && !order.paymentMethods?.length) {
            dispatch(getPaymentMethods());
        }

        if (order.items.length === 0) {
            navigate(trans(appRoutes[AppRoute.checkoutOrder].path));
        }
    }, [tab, order.id]);

    useEffect((): void => {
        if (order.paymentEnvironmentUrl) {
            if (!isSSR) {
                setIsRedirectingToPay(true);
                window.open(order.paymentEnvironmentUrl, LinkTarget.self);
            }
        }
    }, [order.paymentEnvironmentUrl]);

    // Handle error navigations, useTimeout to fire at end of callstack to prevent race condition
    useTimeout((): void => {
        if (registrationError) {
            navigate(trans(appRoutes[AppRoute.checkoutDetails].path));
        }
    }, 0, [registrationError]);

    const handleAddCouponCode = async (data: CouponCodeFormData): Promise<void> => {
        await dispatch(addCouponCodeToOrder(data));
    };

    const handleRemoveCouponCode = (): void => {
        dispatch(removeCouponCodeFromOrder());
    };

    const handleToCheckout = async (): Promise<void> => {
        sendGTMEvent(GTMEvent.checkoutHasBegun, cart);

        navigate(trans(appRoutes[AppRoute.checkoutDetails].path));
    };

    const handleSubmitDetails = async (formData: CheckoutDetailsFormData): Promise<void> => {
        const addressDefaults = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            country: defaultCountry.toUpperCase(),
        };

        const shippingAddress: CheckoutAddress = {
            ...addressDefaults,
            label: trans(`entities.address.type.${AddressType.shipping}`),
            street: formData.shippingStreet,
            houseNumber: formData.shippingHouseNumber,
            addition: formData.shippingAddition,
            city: formData.shippingCity,
            postalCode: formData.shippingPostalCode.toUpperCase(),
        };

        const billingAddress: CheckoutAddress = formData.hasBillingAddress
            ? {
                ...addressDefaults,
                label: trans(`entities.address.type.${AddressType.billing}`),
                street: formData.billingStreet,
                houseNumber: formData.billingHouseNumber,
                addition: formData.billingAddition,
                city: formData.billingCity,
                postalCode: formData.billingPostalCode.toUpperCase(),
            }
            : {
                ...shippingAddress,
                label: trans(`entities.address.type.${AddressType.billing}`),
            };

        const checkoutData: CheckoutFormData = {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phoneNumber: formData.phoneNumber,
            shippingAddress,
            billingAddress,
        };

        // Store data in state so it can be used in later steps of checkout
        setCheckoutFormData(checkoutData);

        await dispatch(updateOrder(checkoutData));

        if (formData.subscribeToNewsletter) {
            if (!executeRecaptcha) {
                console.error('[reCAPTCHA not available]: Bot detected');
                return;
            }

            const recaptchaToken = await executeRecaptcha();

            const newsletterBody: NewsletterFormData = {
                recaptcha: recaptchaToken,
                email: checkoutData.email,
                firstName: checkoutData.firstName,
            };

            await dispatch(signUpForNewsletter(newsletterBody));

            sendGTMEvent(GTMEvent.signUp, {
                method: 'newsletter',
            });
        }

        if (formData.createAccount && formData.password) {
            await dispatch(registerUserDuringCheckout({
                ...formData,
                password: formData.password,
            }));

            sendGTMEvent(GTMEvent.signUp, {
                method: 'account',
            });
        }

        navigate(trans(appRoutes[AppRoute.checkoutPayment].path));
    };

    const handleSubmitPayment = async (): Promise<void> => {
        if (!paymentStepData) {
            console.error('No payment data');
            return;
        }

        // @TODO: Add check if there is new data, if so call `updateOrder()`
        if (checkoutFormData) {
            await dispatch(updateOrder({
                ...checkoutFormData,
                shippingAddress: paymentStepData.shippingAddress,
                billingAddress: paymentStepData.billingAddress,
            }));
        }

        const payOrderData = {
            expectedPriceInCents: order.totalPrice,
            paymentMethodId: paymentStepData.paymentMethodId,
            giftCardNumber: paymentStepData.giftCardNumber,
            giftCardPin: paymentStepData.giftCardPin,
            notes: paymentStepData.notes,
        };

        await dispatch(payOrder(payOrderData));
    };

    const handleUpdateCartItemQuantity = async (formData: ProductQuantitySubmitFormData): Promise<void> => {
        await dispatch(updateProductVariantInCart(formData));
    };

    const handleRemoveItemFromCart = async (id: string): Promise<void> => {
        await dispatch(removeProductVariantFromCart(id));

        const cartItem = order.items.find(item => item.id === id);

        if (!cartItem) {
            return;
        }

        const gtmProduct = generateCartItemToGTMProduct(cartItem);

        sendGTMEvent(GTMEvent.removeItemFromCart, gtmProduct);
    };

    const handleClearCart = async (): Promise<void> => {
        for (let i = 0; i < order.items.length; i += 1) {
            const product = order.items[i];
            const gtmProduct = generateCartItemToGTMProduct(product);

            sendGTMEvent(GTMEvent.removeItemFromCart, gtmProduct);
        }

        await dispatch(clearCart());
    };

    return (
        <Checkout
            {...order}
            isInitiallyLoading={cartIsInitiallyLoading}
            isLoading={isLoading}
            disablePayButton={isRedirectingToPay || totalExceedsMaximum}
            tab={tab}
            customer={customer}
            addresses={orderAddresses}
            paymentMethods={order?.paymentMethods}
            uspItems={MOCK_USPS} // TODO: Replace with data from Strapi
            onAddCouponCode={handleAddCouponCode}
            onRemoveCouponCode={handleRemoveCouponCode}
            onGoToCheckout={handleToCheckout}
            onSubmitDetails={handleSubmitDetails}
            onSubmitPayment={handleSubmitPayment}
            setPaymentData={setPaymentStepData}
            onUpdateCartItem={handleUpdateCartItemQuantity}
            onRemoveItemFromCart={handleRemoveItemFromCart}
            onClearCart={handleClearCart}
        />
    );
};
